import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card, Badge, Spinner, EmptyState } from "@/components/ui";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import toast from "react-hot-toast";
import { useAppStore } from "@/store/appStore";
import { useAuthStore } from "@/store/authStore";
import clsx from "clsx";

const STATUS_COLORS = { pending: "yellow", approved: "green", rejected: "red" };
const STATUS_LABELS = { pending: "⏳ Ausstehend", approved: "✅ Genehmigt", rejected: "❌ Abgelehnt" };
const TYPE_LABELS   = { irl: "🌍 IRL-Aufgabe", digital: "💻 Digital", quiz: "⚡ Quiz" };

export default function Tasks() {
  const qc = useQueryClient();
  const showReward    = useAppStore((s) => s.showReward);
  const { refreshMe } = useAuthStore();
  const fileRef = useRef();
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [filter, setFilter] = useState("open"); // open | done

  const { data, isLoading } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => api.get("/tasks").then((r) => r.data),
  });

  const complete = useMutation({
    mutationFn: ({ taskId, note, proofFile }) => {
      const fd = new FormData();
      fd.append("note", note);
      if (proofFile) fd.append("proof", proofFile);
      return api.post(`/tasks/${taskId}/complete`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    onSuccess: (res) => {
      if (res.data.autoApproved) {
        showReward({ xp: res.data.xpEarned, coins: res.data.coinsEarned, reason: "Aufgabe abgeschlossen!" });
        refreshMe();
      } else {
        toast.success("📬 Abgabe eingereicht — dein Lehrer prüft es!");
      }
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["student", "me"] });
      setSelected(null);
      setNote("");
      if (fileRef.current) fileRef.current.value = "";
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler beim Einreichen"),
  });

  if (isLoading) return <div className="flex justify-center pt-20"><Spinner size="lg" /></div>;

  const tasks = data?.tasks || [];
  const open  = tasks.filter((t) => !t.completion);
  const done  = tasks.filter((t) => !!t.completion);
  const shown = filter === "open" ? open : done;

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black">📌 Aufgaben</h1>
          <p className="text-xs text-gray-400 mt-0.5">{open.length} offen · {done.length} erledigt</p>
        </div>
        <div className="flex gap-1.5">
          {["open", "done"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={clsx(
                "px-3 py-1.5 rounded-xl text-xs font-bold border transition-all",
                f === filter ? "bg-violet-600 border-violet-500 text-white" : "bg-nexus-s2 border-nexus-border text-gray-400"
              )}
            >
              {f === "open" ? "📌 Offen" : "✅ Erledigt"}
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <EmptyState
          icon={filter === "open" ? "🎉" : "📌"}
          title={filter === "open" ? "Alle Aufgaben erledigt!" : "Noch nichts erledigt"}
          desc={filter === "open" ? "Super! Schau morgen wieder rein." : "Erledige deine erste Aufgabe!"}
        />
      ) : (
        <div className="space-y-3">
          {shown.map((t) => {
            const comp = t.completion;
            const diff = ["", "⭐", "⭐⭐", "⭐⭐⭐"][t.difficulty] || "";
            return (
              <Card
                key={t.id}
                hover={!comp}
                onClick={() => !comp && setSelected(t)}
                className={clsx("p-4", comp?.status === "approved" && "opacity-70")}
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0 mt-0.5">{t.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={clsx("font-black text-sm leading-tight", comp && "line-through text-gray-500")}>{t.title}</p>
                      {comp && <Badge color={STATUS_COLORS[comp.status]}>{STATUS_LABELS[comp.status]}</Badge>}
                    </div>
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed">{t.description}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <Badge color="blue">{TYPE_LABELS[t.type]}</Badge>
                      {diff && <span className="text-[10px] text-gray-500">{diff}</span>}
                      {t.requiresProof && <Badge color="cyan">📷 Foto nötig</Badge>}
                    </div>
                    {comp?.rejectionNote && (
                      <p className="text-xs text-red-400 mt-2 bg-red-500/10 border border-red-500/20 rounded-lg p-2">
                        ❌ {comp.rejectionNote}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-black text-violet-400">+{t.xpReward} XP</p>
                    <p className="text-[10px] text-yellow-400">+{t.coinsReward} 🪙</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Submit modal */}
      <Modal open={!!selected} onClose={() => { setSelected(null); setNote(""); }} title={`📌 ${selected?.title || ""}`}>
        {selected && (
          <div className="space-y-4">
            <div className="bg-nexus-s2 rounded-xl p-4">
              <p className="text-sm text-gray-300 leading-relaxed">{selected.description}</p>
              <div className="flex gap-2 mt-3 flex-wrap">
                <Badge color="blue">{TYPE_LABELS[selected.type]}</Badge>
                <span className="text-xs font-bold text-violet-400">+{selected.xpReward} XP</span>
                <span className="text-xs font-bold text-yellow-400">+{selected.coinsReward} 🪙</span>
              </div>
            </div>

            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Kurze Notiz / Erklärung (optional)..."
              className="w-full bg-nexus-s2 border border-nexus-border rounded-xl p-3 text-sm text-white placeholder-gray-500 resize-none h-20 focus:outline-none focus:border-violet-500"
            />

            {selected.requiresProof && (
              <div>
                <p className="text-xs font-bold text-cyan-400 mb-2">📷 Beweis-Foto erforderlich</p>
                <label className="flex items-center gap-2 cursor-pointer p-3 bg-nexus-s2 border border-dashed border-cyan-500/40 rounded-xl hover:bg-nexus-s3 transition-colors">
                  <span className="text-lg">📎</span>
                  <span className="text-sm text-gray-400">Datei auswählen</span>
                  <input ref={fileRef} type="file" className="hidden" accept="image/*,video/*,application/pdf" />
                </label>
              </div>
            )}

            <Button
              full
              loading={complete.isPending}
              onClick={() =>
                complete.mutate({
                  taskId: selected.id,
                  note,
                  proofFile: fileRef.current?.files?.[0],
                })
              }
            >
              🚀 Einreichen
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
